import { useEffect, useMemo, useState } from 'react';
import { CalendarCheck2, Loader2, Phone } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const toDateOnly = (value) => {
  const d = value instanceof Date ? value : new Date(value);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateOnly = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const getStatusColor = (status) => {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    new: 'bg-[#C97A5E]/10 text-[#C97A5E]',
    contacted: 'bg-blue-100 text-blue-800',
    qualified: 'bg-purple-100 text-purple-800',
    trial_pack_shared: 'bg-orange-100 text-orange-800',
    converted: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

export default function LeadTasksPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [staff, setStaff] = useState([]);
  const [staffFilter, setStaffFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const dateRange = useMemo(() => {
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    if (filter === 'today') {
      const date = toDateOnly(startOfToday);
      return { date_from: date, date_to: date };
    }
    if (filter === 'next7') {
      const start = toDateOnly(startOfToday);
      const endDate = new Date(startOfToday);
      endDate.setDate(endDate.getDate() + 7);
      const end = toDateOnly(endDate);
      return { date_from: start, date_to: end };
    }
    if (filter === 'overdue') {
      const endDate = new Date(startOfToday);
      endDate.setDate(endDate.getDate() - 1);
      return { date_to: toDateOnly(endDate) };
    }
    return {};
  }, [filter]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getLeadTasks({
        ...dateRange,
        sales_staff_id: staffFilter === 'all' ? undefined : staffFilter,
        page,
        page_size: pageSize,
      });
      setTasks(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch lead tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await adminApi.getSalesStaff();
      setStaff(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch sales staff');
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [filter, staffFilter]);

  useEffect(() => {
    fetchTasks();
  }, [filter, staffFilter, page]);

  const todayDate = toDateOnly(new Date());

  return (
    <div className="space-y-6" data-testid="lead-tasks-page">
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Lead Tasks</h1>
        <p className="text-[#A8A8A8] mt-1">Latest follow-ups by lead</p>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-2 items-center">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              className={cn('border-[#E5E5E5]', filter === 'all' && 'bg-[#C97A5E] hover:bg-[#B06850]')}
              onClick={() => setFilter('all')}
              data-testid="tasks-filter-all"
            >
              All
            </Button>
            <Button
              variant={filter === 'today' ? 'default' : 'outline'}
              className={cn('border-[#E5E5E5]', filter === 'today' && 'bg-[#C97A5E] hover:bg-[#B06850]')}
              onClick={() => setFilter('today')}
              data-testid="tasks-filter-today"
            >
              Today
            </Button>
            <Button
              variant={filter === 'next7' ? 'default' : 'outline'}
              className={cn('border-[#E5E5E5]', filter === 'next7' && 'bg-[#C97A5E] hover:bg-[#B06850]')}
              onClick={() => setFilter('next7')}
              data-testid="tasks-filter-next7"
            >
              Next 7 Days
            </Button>
            <Button
              variant={filter === 'overdue' ? 'default' : 'outline'}
              className={cn('border-[#E5E5E5]', filter === 'overdue' && 'bg-[#C97A5E] hover:bg-[#B06850]')}
              onClick={() => setFilter('overdue')}
              data-testid="tasks-filter-overdue"
            >
              Overdue
            </Button>
            <div className="flex-1 min-w-[200px] sm:max-w-[260px]">
              <Select value={staffFilter} onValueChange={setStaffFilter}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Filter by staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staff.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-[#A8A8A8]">
              <CalendarCheck2 className="w-12 h-12 mb-4" />
              <p>No tasks found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Lead</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Contact</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Message</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Status</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Follow-up</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {tasks.map((task) => {
                    const followUp = task.follow_up_date ? toDateOnly(task.follow_up_date) : null;
                    const isOverdue = followUp && followUp < todayDate;
                    return (
                      <tr key={`${task.lead_id}-${task.action_id}`} className="hover:bg-[#FAFAF9]/50 transition-colors">
                        <td className="py-4 px-6">
                          <div className="space-y-1">
                            <p className="font-medium text-[#2D2420]">{task.lead_name || 'Unknown'}</p>
                            {task.lead_ref_no && (
                              <p className="text-xs text-[#A8A8A8]">Ref: {task.lead_ref_no}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          {task.lead_phone ? (
                            <div className="flex items-center gap-2 text-sm text-[#5C4033]">
                              <Phone className="w-4 h-4" /> {task.lead_phone}
                            </div>
                          ) : (
                            <span className="text-sm text-[#A8A8A8]">-</span>
                          )}
                        </td>
                        <td className="py-4 px-6 max-w-xs">
                          <p className="text-sm text-[#2D2420]">{task.message || '-'}</p>
                        </td>
                        <td className="py-4 px-6">
                          <Badge className={cn('text-xs', getStatusColor(task.lead_status))}>
                            {task.lead_status || 'pending'}
                          </Badge>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-[#2D2420]">{formatDateOnly(task.follow_up_date)}</span>
                            {isOverdue && (
                              <Badge className="bg-red-100 text-red-700 text-xs">Overdue</Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        {!loading && total > 0 && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4 border-t border-[#E5E5E5]">
            <p className="text-sm text-[#A8A8A8]">
              Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total}
            </p>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((prev) => Math.max(1, prev - 1));
                    }}
                    className={page === 1 ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
                <PaginationItem>
                  <span className="px-3 text-sm text-[#5C4033]">
                    Page {page} of {Math.max(1, Math.ceil(total / pageSize))}
                  </span>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((prev) => Math.min(Math.max(1, Math.ceil(total / pageSize)), prev + 1));
                    }}
                    className={page >= Math.max(1, Math.ceil(total / pageSize)) ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </Card>
    </div>
  );
}
